/**
 * SendHistory (E3 Item 5b) — the send record on the lead file, read from
 * email_log, newest first. Visible to any employee (open sales page).
 *
 * Status is visually distinct so a BOUNCE can never be mistaken for a success:
 * delivered = green, sent (accepted, not yet confirmed) = amber, bounced / failed
 * / complained = red. Opens and clicks are shown as SOFT engagement signals —
 * opens are muted with a caveat (Apple Mail Privacy Protection pre-fetches images,
 * so opens over-report), clicks read a little stronger (reliable). Neither is
 * presented as proof the client read the message.
 *
 * Failed and bounced rows get a re-send control that routes back through the one
 * send layer (a new attempt writes a NEW row — the original is preserved).
 */

import React, { useState } from 'react';
import { tintOf } from '../lib/theme';
import { useApi, useApiClient } from '../hooks/useApi';
import { formatDateTime } from '../lib/utils';
import { Icons } from '../lib/icons';
import { emailTypeLabel, sendStatus } from '../lib/feedLabels';
import { EstimateSendConfirm } from './EstimateSendConfirm';

/**
 * Labels come from the ONE shared map now (lib/feedLabels).
 *
 * This file used to carry its own list of EIGHT email types while
 * email_log.email_type takes ELEVEN, and the three it had never heard of —
 * document_completed, signature_request, document_batch — fell through its
 * `?? r.email_type` fallback and printed the raw column value on a page a client
 * file is judged by. A fallback that prints the raw value is why it went
 * unnoticed for so long: it looks like a label rather than a miss.
 */

export function SendHistory({ leadId, users, isAdmin, refreshSignal }: { leadId: string; users: any[]; isAdmin: boolean; refreshSignal?: number }) {
  const client = useApiClient();
  const { data, refresh } = useApi<any[]>(async (api) => (await api.getEmailLog(leadId)).data || [], { dependencies: [leadId, refreshSignal] });
  const rows = data || [];
  const [busyId, setBusyId] = useState<string | null>(null);
  const [msg, setMsg] = useState<{ ok: boolean; text: string; onOverride?: () => void } | null>(null);
  // An estimate re-send asks first, like every estimate send (EstimateSendConfirm).
  const [confirmEstimate, setConfirmEstimate] = useState<any | null>(null);

  const nameOf = (id: string | null) => {
    const u = users?.find((x) => x.id === id);
    return u ? `${u.first_name ?? ''} ${u.last_name ?? ''}`.trim() || u.email : '—';
  };

  const canResend = (r: any) => {
    if (r.status !== 'failed' && r.status !== 'bounced') return false;
    if (r.email_type === 'move_booked') return false; // internal — re-booking isn't a re-send
    if (r.email_type === 'new_lead') return false;     // auto on creation — no manual re-send
    if ((r.email_type === 'contractor_estimate' || r.email_type === 'contractor_work_order') && !isAdmin) return false;
    if (r.email_type === 'invoice' && !r.target_id) return false;
    if (r.email_type === 'payment_receipt' && !r.payment_id) return false;
    return true;
  };

  const doResend = async (r: any, override: boolean) => {
    if (r.email_type === 'estimate') await client.emailEstimate(leadId, override);
    else if (r.email_type === 'invoice') await client.sendInvoice(r.target_id, override);
    else if (r.email_type === 'inventory_request') await client.sendInventoryRequest(leadId, override);
    else if (r.email_type === 'contractor_estimate') await client.sendContractorEstimate(leadId, override);
    else if (r.email_type === 'contractor_work_order') await client.sendContractorWorkOrder(leadId, override);
    else if (r.email_type === 'payment_receipt') await client.resendPaymentReceipt(r.payment_id, override);
    // Just the client email (only=client): the lead is not booked again.
    else if (r.email_type === 'booking_confirmation') {
      const res = await client.bookLead(leadId, false, override, undefined, { sendClient: true, only: 'client' });
      if (res.data?.clientEmailError) {
        throw Object.assign(new Error(res.data.clientEmailError), res.data.clientEmailBlocked ? { code: 'SUPPRESSED' } : {});
      }
    }
    else throw new Error('This message type cannot be re-sent here.');
  };

  const resend = async (r: any, override = false) => {
    if (r.email_type === 'estimate' && !override && confirmEstimate !== r) { setConfirmEstimate(r); return; }
    setConfirmEstimate(null);
    setBusyId(r.id);
    setMsg(null);
    try {
      await doResend(r, override);
      // QUIET refresh: the history stays on screen until the new list replaces
      // it. The send has already happened, so a failed refresh is not a failed
      // send — it says so separately instead of blaming the email.
      try {
        await refresh();
        setMsg({ ok: true, text: 'Re-sent — a new entry is at the top.' });
      } catch {
        setMsg({ ok: true, text: 'Re-sent. The list could not refresh — reopen the lead to see the new entry.' });
      }
    } catch (e: any) {
      if (e?.code === 'SUPPRESSED') setMsg({ ok: false, text: e.message, onOverride: () => resend(r, true) });
      else setMsg({ ok: false, text: e?.message || 'Re-send failed — please try again.' });
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div style={{ background: 'var(--crm-panel)', border: '1px solid var(--crm-line)', borderRadius: '10px', overflow: 'hidden' }}>
      {confirmEstimate && (
        <EstimateSendConfirm leadId={leadId} onConfirm={() => void resend(confirmEstimate)} onCancel={() => setConfirmEstimate(null)} />
      )}
      <div style={{ padding: '11px 14px', borderBottom: '1px solid var(--crm-line)', fontSize: '13px', fontWeight: 600 }}>Send history</div>

      {msg && (
        <div style={{ padding: '8px 14px', fontSize: '12px', color: msg.ok ? 'var(--crm-green)' : 'var(--crm-red)', borderBottom: '1px solid var(--crm-panel-2)', display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
          <span>{msg.text}</span>
          {msg.onOverride && (
            <button onClick={msg.onOverride} style={{ padding: '3px 10px', background: 'var(--crm-panel-2)', border: '1px solid var(--crm-amber-line)', color: 'var(--crm-amber)', borderRadius: '6px', cursor: 'pointer', fontSize: '11px', fontWeight: 600 }}>Send anyway</button>
          )}
        </div>
      )}

      {rows.length === 0 && <div style={{ padding: '18px', textAlign: 'center', color: 'var(--crm-dim)', fontSize: '12px' }}>Nothing sent yet.</div>}

      {rows.map((r, i) => {
        const s = sendStatus(r.status);
        return (
          <div key={r.id} style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '10px 14px', borderBottom: i < rows.length - 1 ? '1px solid var(--crm-panel-2)' : 'none' }}>
            <span style={{ flex: '0 0 auto', fontSize: '10px', fontWeight: 700, color: s.color, background: `${tintOf(s.color, 13)}`, border: `1px solid ${tintOf(s.color, 33)}`, padding: '2px 8px', borderRadius: '999px', textTransform: 'uppercase', letterSpacing: '.4px' }}>{s.label}</span>

            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: '12px', color: 'var(--crm-text)' }}>
                {emailTypeLabel(r.email_type)} <span style={{ color: 'var(--crm-muted)', fontFamily: "'DM Mono', monospace" }}>· {r.recipient}</span>
              </div>
              <div style={{ fontSize: '11px', color: 'var(--crm-dim)', marginTop: '2px' }}>
                {formatDateTime(r.created_at)} · {nameOf(r.sent_by)}
                {r.status === 'bounced' && r.bounce_reason ? <span style={{ color: 'var(--crm-red)' }}> · {r.bounce_reason}</span> : ''}
                {(r.status === 'failed' || r.status === 'blocked') && r.error ? <span style={{ color: r.status === 'blocked' ? 'var(--crm-amber)' : 'var(--crm-red)' }}> · {r.error}</span> : ''}
              </div>
              {/* Engagement signal — clicks only. Open tracking is disabled in Resend
                  (click tracking only, for deliverability), so the "opened" signal
                  will never populate; showing a permanently-blank indicator would
                  read as a bug. The opened_at/open_count columns + webhook handling
                  are kept — only this UI element is hidden. */}
              {r.clicked_at && (
                <div style={{ marginTop: '3px', fontSize: '10px' }}>
                  <span title="Link clicks are reliable." style={{ color: 'var(--crm-cyan)', display: 'inline-flex', alignItems: 'center', gap: '3px' }}>
                    <Icons.click size={11} /> Clicked{r.click_count > 1 ? ` ·${r.click_count}` : ''}
                  </span>
                </div>
              )}
            </div>

            {canResend(r) && (
              <button
                onClick={() => resend(r)}
                disabled={busyId === r.id}
                style={{ flex: '0 0 auto', padding: '5px 11px', background: 'var(--crm-panel-2)', border: '1px solid var(--crm-accent-soft-line)', color: 'var(--crm-accent)', borderRadius: '6px', cursor: busyId === r.id ? 'default' : 'pointer', fontSize: '11px', fontWeight: 600, opacity: busyId === r.id ? 0.6 : 1 }}
              >
                {busyId === r.id ? 'Sending…' : 'Re-send'}
              </button>
            )}
          </div>
        );
      })}
    </div>
  );
}
